// src/pages/PrizeStructure/index.tsx
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Modal, ModalBody } from 'reactstrap';
import BreadCrumb from 'Components/Common/BreadCrumb';
import PrizeStructureTable from './PrizeStructureTable';
import AddPrizeTierModal from './AddPrizeTierModal';
import {
  fetchPrizeTierConfigs,
  updatePrizeTierConfigs,
  createPrizeTierConfig,
  initializePrizeTierConfigs,
  deletePrizeTierConfig,
} from 'slices/prizeStructure/thunk';
import { setRegion, setTierField } from 'slices/prizeStructure/reducer';
import { PrizeTierConfig, NewPrizeTierConfigInput } from './types';
import { fetchSystemRegion } from 'Components/constants/regions';
import { useBrandingTitle } from '../../hooks';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('prize_structure');

const PrizeStructurePage: React.FC = () => {
  useBrandingTitle(st('co_cau_giai_thuong_text'));

  const location = useLocation();
  const dispatch = useDispatch();
  const { region, tiers, isDirty, loading, submitting, creating, initializing, deletingCode } = useSelector(
    (state: any) => state.PrizeStructure
  );
  const [pendingDelete, setPendingDelete] = useState<PrizeTierConfig | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Yêu cầu bổ sung (README_UPDATE.md) — Miền phải lấy từ ĐÚNG 1 nguồn cấu hình (API), không đoán từ
  // cache (cache hết hạn/lệch → load nhầm cơ cấu giải thưởng của miền khác).
  const [regionConfirmed, setRegionConfirmed] = useState(false);
  const [regionError, setRegionError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchSystemRegion().then((r) => {
      if (!alive) return;
      if (r) {
        if (r !== region) dispatch(setRegion(r));
        setRegionError(null);
        setRegionConfirmed(true);
      } else {
        setRegionConfirmed(false);
        setRegionError(st('he_thong_chua_cau_hinh_mien_text'));
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (regionConfirmed) {
      dispatch(fetchPrizeTierConfigs(region) as any);
    }
  }, [dispatch, regionConfirmed, region]);

  const handleFieldChange = (code: string, field: keyof PrizeTierConfig, value: any) => {
    dispatch(setTierField({ code, field, value }));
  };

  const handleSave = () => {
    const payload = tiers.map((t: PrizeTierConfig) => ({
      code: t.code,
      quantity: t.quantity,
      valuePerTicket: t.valuePerTicket,
      digitCondition: t.digitCondition,
      matchingRule: t.matchingRule,
      matchingPosition: t.matchingRule === 'exact' ? null : t.matchingPosition,
      matchingDigits: t.matchingRule === 'exact' ? null : t.matchingDigits,
    }));
    dispatch(updatePrizeTierConfigs({ region, tiers: payload }) as any);
  };

  const handleInitialize = () => {
    dispatch(initializePrizeTierConfigs(region) as any);
  };

  const handleAddTier = (tier: NewPrizeTierConfigInput) => {
    const result: any = dispatch(createPrizeTierConfig({ region, tier }) as any);
    result
      .unwrap()
      .then(() => setIsAddModalOpen(false))
      .catch(() => {
        // Lỗi đã toast ở thunk — giữ modal mở để người dùng sửa lại input
      });
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    dispatch(deletePrizeTierConfig({ region, code: pendingDelete.code }) as any);
    setPendingDelete(null);
  };

  const isEmpty = !loading && tiers.length === 0;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('co_cau_giai_thuong_text')} pageTitle={st('tra_thuong_text')} />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    {st('co_cau_giai_thuong_text')}
                    <span className="badge bg-primary-subtle text-primary fs-12 fw-normal">{region}</span>
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                    {!isEmpty && (
                      <>
                      {PageActionPermission.canDisplay(location.pathname, 'button-add') && (
                      <Button
                        color="outline-primary"
                        data-action-code="button-add"
                        data-action-name="Thêm hạng giải"
                        onClick={() => setIsAddModalOpen(true)}
                      >
                        <i className="ri-add-line me-1"></i>
                        {st('them_hang_giai_text')}
                      </Button>
                      )}
                      <Link to="/draw-result" className="btn btn-outline-primary">
                        {st('xem_ket_qua_quay_so_button')} <i className="ri-arrow-right-line align-middle"></i>
                      </Link>
                      {PageActionPermission.canDisplay(location.pathname, 'button-save') && (
                      <Button
                        color="primary"
                        data-action-code="button-save"
                        data-action-name="Lưu cấu hình"
                        onClick={handleSave}
                        disabled={!isDirty || submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            {st('dang_luu_text')}
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line me-1"></i>
                            {st('luu_cau_hinh_text')}
                          </>
                        )}
                      </Button>
                      )}
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardBody>
                  {regionError ? (
                    <div className="alert alert-warning mb-0">
                      <i className="ri-error-warning-line align-bottom me-1" />
                      {regionError}
                    </div>
                  ) : isEmpty ? (
                    <div className="text-center py-5">
                      <i className="ri-trophy-line text-muted" style={{ fontSize: '48px' }}></i>
                      <p className="text-muted mt-2 mb-3">
                        {st('chua_co_co_cau_giai_thuong_text')}
                      </p>
                      {PageActionPermission.canDisplay(location.pathname, 'button-initialize') && (
                      <Button
                        color="primary"
                        data-action-code="button-initialize"
                        data-action-name="Khởi tạo cơ cấu giải thưởng mặc định"
                        onClick={handleInitialize}
                        disabled={initializing}
                      >
                        {initializing ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            {st('dang_khoi_tao_text')}
                          </>
                        ) : (
                          <>
                            <i className="ri-add-circle-line me-1"></i>
                            {st('khoi_tao_co_cau_giai_thuong_text')}
                          </>
                        )}
                      </Button>
                      )}
                    </div>
                  ) : (
                    <PrizeStructureTable
                      tiers={tiers}
                      loading={loading}
                      deletingCode={deletingCode}
                      onFieldChange={handleFieldChange}
                      onDelete={setPendingDelete}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <AddPrizeTierModal
        isOpen={isAddModalOpen}
        submitting={creating}
        onSubmit={handleAddTier}
        onClose={() => setIsAddModalOpen(false)}
      />

      <Modal isOpen={!!pendingDelete} toggle={() => setPendingDelete(null)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{st('xoa_hang_giai_nay_title')}</h4>
              <p className="text-muted mx-4 mb-0">
                {st('ban_co_chac_chan_muon_xoa_text')} <b>{pendingDelete?.displayName}</b> {st('khoi_co_cau_giai_thuong_khong_text')}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" className="w-sm" onClick={() => setPendingDelete(null)}>
              {st('dong_button')}
            </Button>
            {PageActionPermission.canDisplay(location.pathname, 'button-delete-confirm') && (
            <Button
              color="danger"
              className="w-sm"
              data-action-code="button-delete-confirm"
              data-action-name="Xác nhận xoá hạng giải"
              onClick={handleConfirmDelete}
            >
              {st('co_xoa_no_button')}
            </Button>
            )}
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default PrizeStructurePage;
